import { useCallback, useEffect, useState } from "react";
import { createAndInitClient, getGlpiClient, setGlpiClient } from "../services/glpiClient";
import { resolveApiUrl, resolveAppToken, settings } from "../services/outlookContext";
import { GlpiError } from "../types/glpi";

export type SessionStatus = "idle" | "missing-token" | "connecting" | "ready" | "error";

export interface UseGlpiSessionResult {
  status: SessionStatus;
  error: string | null;
  retry: () => Promise<void>;
  reconnect: () => Promise<void>;
  reset: () => Promise<void>;
}

/**
 * Gère le cycle de vie de la session GLPI :
 * - Au mount : si un user_token est présent dans roamingSettings, initialise un client.
 * - Sinon : status "missing-token" -> SettingsScreen.
 * - killSession au beforeunload du task pane.
 */
export function useGlpiSession(): UseGlpiSessionResult {
  const [status, setStatus] = useState<SessionStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(async () => {
    const userToken = settings.getUserToken();
    if (!userToken) {
      setStatus("missing-token");
      setError(null);
      return;
    }
    const apiUrl = resolveApiUrl();
    const appToken = resolveAppToken();

    // Diagnostic : on logge la source effective de chaque paramètre.
    const storedUrl = settings.getApiUrl();
    // eslint-disable-next-line no-console
    console.info("[GLPI] config effective", {
      apiUrl,
      apiUrlSource: storedUrl ? "roamingSettings" : "env (.env.development)",
      envApiUrl: process.env.GLPI_API_URL,
      storedApiUrl: storedUrl || "(non défini)",
      appTokenLength: appToken.length,
    });

    if (!apiUrl || !appToken) {
      setStatus("error");
      setError("Configuration manquante : URL GLPI ou App-Token absents.");
      return;
    }

    setStatus("connecting");
    setError(null);
    try {
      await createAndInitClient({ apiUrl, appToken, userToken });
      setStatus("ready");
    } catch (e) {
      const msg = e instanceof GlpiError ? `${e.code} - ${e.message}` : (e as Error).message;
      setStatus("error");
      setError(msg);
    }
  }, []);

  const reset = useCallback(async () => {
    const c = getGlpiClient();
    if (c) {
      try {
        await c.killSession();
      } catch {
        // ignore
      }
      setGlpiClient(null);
    }
    await settings.setUserToken(null);
    setStatus("missing-token");
    setError(null);
  }, []);

  useEffect(() => {
    void connect();

    // Cleanup : killSession quand le task pane se ferme.
    const handler = () => {
      const c = getGlpiClient();
      if (c) {
        // killSession est synchrone "best-effort" ici, on n'attend pas la réponse.
        void c.killSession();
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status,
    error,
    retry: connect,
    reconnect: connect,
    reset,
  };
}
